import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommentsService } from '../../service/comments.service';
import { ImportedTridy } from '../../service/tridys.service';
import { TridyQrErrorCorrection, TridyQrExportService, TridyQrExportSettings, TridyQrFormat } from '../../service/tridy-qr-export.service';
import { insertQrFilenamePlaceholder, qrFilenameExtension, qrFilenameKeyLabel, qrFilenameKeyParent, qrFilenameSchemaKeys, qrFilenameTemplateError, QR_FILENAME_DATE_KEYS } from '../qr-filename-template';

@Component({
  selector: 'app-tridy-qr-export-dialog',
  standalone: false,
  templateUrl: './tridy-qr-export-dialog.component.html',
  styleUrls: ['./tridy-qr-export-dialog.component.css']
})
export class TridyQrExportDialogComponent {
  tridys: ImportedTridy[] = [];
  qrFormat: TridyQrFormat = 'cmyk-pdf';
  qrPrintSizeMm = 50;
  qrDpi = 300;
  qrQuietZoneModules = 4;
  qrErrorCorrection: TridyQrErrorCorrection = 'M';
  qrJpegQuality = 95;
  qrFilenamePrefix = 'dpp-qr-codes';
  filenameTemplate = '';
  readonly dateFilenameKeys = QR_FILENAME_DATE_KEYS;
  readonly filenameKeyLabel = qrFilenameKeyLabel;
  readonly filenameKeyParent = qrFilenameKeyParent;
  exporting = false;
  progress = { completed: 0, total: 0 };

  constructor(
    private dialogRef: MatDialogRef<TridyQrExportDialogComponent>,
    private qrExportService: TridyQrExportService,
    private commentsService: CommentsService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.dialogRef.disableClose = true;
    this.tridys = data?.tridys || [];
    this.qrFilenamePrefix = data?.filenamePrefix || 'dpp-qr-codes';
  }

  get qrFilenameKeys(): string[] {
    const keys = new Set<string>();
    for (const contentType of this.data?.contentTypes || []) {
      qrFilenameSchemaKeys(contentType?.schema).forEach(key => keys.add(key));
    }
    this.tridys.forEach(tridy => this.collectKeys(tridy.payload || {}, '', keys));
    return Array.from(keys).sort();
  }

  get filenameTemplateError(): string | undefined { return qrFilenameTemplateError(this.filenameTemplate); }
  get filenameExtension(): string { return qrFilenameExtension(this.qrFormat); }

  insertFilenameKey(key: string, input: HTMLInputElement): void {
    this.filenameTemplate = insertQrFilenamePlaceholder(this.filenameTemplate, key, input);
  }

  get isPdfExport(): boolean { return this.qrFormat === 'cmyk-pdf'; }
  get exportPercent(): number {
    if (!this.progress.total) return 0;
    return Math.round((this.progress.completed / this.progress.total) * 100);
  }

  close(): void {
    if (!this.exporting) this.dialogRef.close(false);
  }

  async download(): Promise<void> {
    if (!this.tridys.length || this.exporting || this.filenameTemplateError) return;
    this.exporting = true;
    this.progress = { completed: 0, total: this.tridys.length };
    try {
      this.commentsService.progressSpin.emit(true);
      const settings: TridyQrExportSettings = {
        format: this.qrFormat,
        widthMm: Math.max(10, Number(this.qrPrintSizeMm) || 30),
        dpi: Math.max(72, Number(this.qrDpi) || 300),
        quietZoneModules: Math.max(4, Number(this.qrQuietZoneModules) || 4),
        errorCorrection: this.qrErrorCorrection,
        jpegQuality: Math.max(1, Number(this.qrJpegQuality) || 95),
        filenamePrefix: this.qrFilenamePrefix,
        filenameTemplate: this.filenameTemplate,
      };

      await this.qrExportService.downloadFromBackend(this.tridys, settings);
      this.progress = { completed: this.tridys.length, total: this.tridys.length };
      this.commentsService.addSnackBar.emit('QR code download started.');
      this.dialogRef.close(true);
    } catch (error: any) {
      const message = await this.getErrorMessage(error);
      console.error('QR export failed', error, message);
      this.commentsService.addSnackBar.emit(message || 'The QR ZIP could not be downloaded. Please try again.');
    } finally {
      this.commentsService.progressSpin.emit(false);
      this.exporting = false;
    }
  }

  private collectKeys(value: Record<string, unknown>, parent: string, keys: Set<string>): void {
    Object.entries(value || {}).forEach(([key, child]) => {
      const fullKey = parent ? `${parent}.${key}` : key;
      if (child && typeof child === 'object' && !Array.isArray(child) && !(child as any).seconds && !(child as any).nanoseconds) {
        this.collectKeys(child as Record<string, unknown>, fullKey, keys);
      } else if (child === null || ['string', 'number', 'boolean'].includes(typeof child)) {
        keys.add(fullKey);
      }
    });
  }

  private async getErrorMessage(error: any): Promise<string> {
    if (error?.error instanceof Blob) {
      const text = await error.error.text();
      try {
        return JSON.parse(text)?.message || text;
      } catch {
        return text;
      }
    }
    return error?.error?.message || error?.message || '';
  }
}
